import { Delaunay } from 'd3-delaunay';
import { clamp01, TAU } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { generatePoints, type PointDistribution } from '../../core/pointSources';
import type { RenderTarget, TechniqueContext } from '../types';
import type { VoronoiValues } from './params';

/**
 * Voronoi tessellation.
 *
 * Uses d3-delaunay (Delaunator underneath) — a sweep-hull triangulation in O(n log n),
 * with the Voronoi diagram derived as its dual. Building it from scratch would be a
 * lot of subtle geometry for no benefit.
 *
 * Lloyd relaxation is applied by moving each point to its cell's centroid and
 * re-triangulating; a couple of iterations turn a clumpy random set into evenly sized
 * cells without losing the organic irregularity that makes the pattern interesting.
 */
export interface VoronoiState {
  points: Float64Array;
  /** Cell polygons after relaxation, as flat coordinate arrays. */
  cells: Float32Array[];
  /** Per-cell signed distance at the site, cached for colouring and clipping. */
  siteDistance: Float32Array;
  areas: Float32Array;
  tint: Float32Array;
  palette: ResolvedPalette;
  relaxationsDone: number;
  built: boolean;
}

function values(ctx: TechniqueContext): VoronoiValues {
  return ctx.values as unknown as VoronoiValues;
}

export function init(ctx: TechniqueContext): VoronoiState {
  const v = values(ctx);
  const flat = generatePoints(v.points, v.distribution as PointDistribution, {
    field: ctx.field,
    width: ctx.width,
    height: ctx.height,
    rng: ctx.rng,
    distance: v.distributionDistance,
  });

  const points = new Float64Array(flat.length);
  points.set(flat);

  const count = points.length / 2;
  return {
    points,
    cells: [],
    siteDistance: new Float32Array(count),
    areas: new Float32Array(count),
    tint: Float32Array.from({ length: count }, () => ctx.rng.next()),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    relaxationsDone: 0,
    built: false,
  };
}

function polygonArea(points: ArrayLike<number>): number {
  let area = 0;
  for (let i = 0; i < points.length; i += 2) {
    const j = (i + 2) % points.length;
    area += points[i] * points[j + 1] - points[j] * points[i + 1];
  }
  return Math.abs(area) / 2;
}

export function step(state: VoronoiState, _dt: number, ctx: TechniqueContext): VoronoiState {
  if (state.built) return state;
  const v = values(ctx);
  if (state.points.length < 6) {
    state.built = true;
    return state;
  }

  const delaunay = new Delaunay(state.points);
  const voronoi = delaunay.voronoi([0, 0, ctx.width, ctx.height]);
  const count = state.points.length / 2;

  // One relaxation pass per step, so a heavy setting still shows progress instead of
  // blocking a frame.
  if (state.relaxationsDone < v.relaxation) {
    for (let i = 0; i < count; i++) {
      const cell = voronoi.cellPolygon(i);
      if (!cell || cell.length < 3) continue;
      let cx = 0;
      let cy = 0;
      for (const [x, y] of cell) {
        cx += x;
        cy += y;
      }
      state.points[i * 2] = cx / cell.length;
      state.points[i * 2 + 1] = cy / cell.length;
    }
    state.relaxationsDone++;
    return state;
  }

  const cells: Float32Array[] = [];
  for (let i = 0; i < count; i++) {
    const cell = voronoi.cellPolygon(i);
    if (!cell || cell.length < 3) {
      cells.push(new Float32Array(0));
      state.areas[i] = 0;
      continue;
    }
    // d3 repeats the first vertex to close the ring; drop it.
    const n = cell.length - 1;
    const flat = new Float32Array(n * 2);
    for (let k = 0; k < n; k++) {
      flat[k * 2] = cell[k][0];
      flat[k * 2 + 1] = cell[k][1];
    }
    cells.push(flat);
    state.areas[i] = polygonArea(flat);
    state.siteDistance[i] = ctx.field.distance(state.points[i * 2], state.points[i * 2 + 1]);
  }

  state.cells = cells;
  state.built = true;
  return state;
}

export function isComplete(state: VoronoiState): boolean {
  return state.built;
}

/** Shrinks a polygon towards its centroid — cheap, stable, and enough for an inset look. */
function insetPolygon(points: Float32Array, amount: number): Float32Array {
  if (amount <= 0 || points.length < 6) return points;
  let cx = 0;
  let cy = 0;
  const n = points.length / 2;
  for (let i = 0; i < n; i++) {
    cx += points[i * 2];
    cy += points[i * 2 + 1];
  }
  cx /= n;
  cy /= n;

  const out = new Float32Array(points.length);
  for (let i = 0; i < n; i++) {
    const dx = points[i * 2] - cx;
    const dy = points[i * 2 + 1] - cy;
    const len = Math.hypot(dx, dy);
    const k = len > amount ? (len - amount) / len : 0;
    out[i * 2] = cx + dx * k;
    out[i * 2 + 1] = cy + dy * k;
  }
  return out;
}

export function draw(state: VoronoiState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);
  let maxArea = 1;
  for (const a of state.areas) if (a > maxArea) maxArea = a;

  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  for (let i = 0; i < state.cells.length; i++) {
    const cell = state.cells[i];
    if (cell.length < 6) continue;

    const distance = state.siteDistance[i];
    if (v.clip === 'outside' && distance < 0) continue;
    if (v.clip === 'inside' && distance > 0) continue;

    let t: number;
    switch (v.colorMode) {
      case 'distance':
        t = clamp01(Math.abs(distance) / reach);
        break;
      case 'area':
        t = clamp01(state.areas[i] / maxArea);
        break;
      case 'angle':
        t =
          (Math.atan2(state.points[i * 2 + 1] - ctx.height / 2, state.points[i * 2] - ctx.width / 2) + Math.PI) / TAU;
        break;
      case 'random':
        t = state.tint[i];
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);

    const shape = v.style === 'inset' ? insetPolygon(cell, v.inset) : cell;
    const path = new Path2D();
    path.moveTo(shape[0], shape[1]);
    for (let k = 2; k < shape.length; k += 2) path.lineTo(shape[k], shape[k + 1]);
    path.closePath();

    if (v.style === 'filled' || v.style === 'inset' || v.style === 'both') {
      c.fillStyle = color;
      c.fill(path);
    }
    if (v.style === 'edges' || v.style === 'both') {
      c.strokeStyle = color;
      c.stroke(path);
    }
  }
}

export function elementCount(state: VoronoiState): number {
  return state.cells.length;
}

export function toSVG(state: VoronoiState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const reach = Math.max(1, ctx.field.maxOutside);
  let maxArea = 1;
  for (const a of state.areas) if (a > maxArea) maxArea = a;
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const parts: string[] = [`<g fill-opacity="${v.opacity}" stroke-opacity="${v.opacity}" stroke-width="${v.lineWidth}" stroke-linejoin="round">`];

  for (let i = 0; i < state.cells.length; i++) {
    const cell = state.cells[i];
    if (cell.length < 6) continue;
    const distance = state.siteDistance[i];
    if (v.clip === 'outside' && distance < 0) continue;
    if (v.clip === 'inside' && distance > 0) continue;

    let t: number;
    switch (v.colorMode) {
      case 'distance':
        t = clamp01(Math.abs(distance) / reach);
        break;
      case 'area':
        t = clamp01(state.areas[i] / maxArea);
        break;
      case 'angle':
        t = (Math.atan2(state.points[i * 2 + 1] - ctx.height / 2, state.points[i * 2] - ctx.width / 2) + Math.PI) / TAU;
        break;
      case 'random':
        t = state.tint[i];
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0) : state.palette.at(t);
    const shape = v.style === 'inset' ? insetPolygon(cell, v.inset) : cell;
    const pts: string[] = [];
    for (let k = 0; k < shape.length; k += 2) pts.push(`${f(shape[k])},${f(shape[k + 1])}`);

    const fill = v.style === 'edges' ? 'none' : color;
    const stroke = v.style === 'filled' ? 'none' : color;
    parts.push(`<polygon points="${pts.join(' ')}" fill="${fill}" stroke="${stroke}"/>`);
  }

  parts.push('</g>');
  return parts.join('\n');
}
